import React, { useState, useEffect, useMemo } from 'react';
import { useDataQuery, useDataEngine } from '@dhis2/app-runtime';
import { Card, CircularLoader, Tooltip } from '@dhis2/ui';
import { ChartLine, Eye } from 'lucide-react';
import { resolveActiveFiscalYear, getFallbackFiscalYear } from '../../GoalsManagement/utils/fiscalYearHelpers';

const TASK_TO_OBJECTIVE_DE = 'plJovUlxfQ0';
const POSITION_TEI_DE = 'SlgO60cMK3F';
const REPORTING_TO_ATTR = 'SjP47qi3JeB';
const POSITION_TE_TYPE = 'UGGo0Ebjl0E';

// Leadership program config from your actual setup
const LEADERSHIP_CONFIG = {
  program: "RSANdetbnWC",
  stages: {
    config: "RF0WnJmJczX",
    weights: "zY4mj9a30tk",
    midYear: "mJ3mC8f2eMv",
    endYear: "TiLy9LFPaNo",
    approvals: "zC1XdAhrAhr",
  },
  de: {
    competencyId: "hM7qEqQBciM",
    competencyCode: "KekX9kvB4b2",
    competencyDefinition: "n9DVY0rbIsl",
    weightValue: "NnO0P1bqPGw",
    ratingScore: "fPIRWdClNPe",
    ratingComment: "SVFdFerDLV1",
    approvalStatus: "iNiXoYwyNUL",
    approvalRemark: "s7aNZvcg8pH",
    positionReference: "SlgO60cMK3F"
  }
};

// Fiscal year program config (same as in Layout)
const FISCAL_YEAR_CONFIG = {
  program: "NkReudUAW4F",
  stage: "htIWgNQWEMm",
  dataElements: {
    start: "U55MqwBcRAe",
    end: "kgdk4NXu4f5"
  }
};


const getCollectionItems = (collection) => collection?.events || collection?.instances || [];

const isItemWithinSelectedFiscalYear = (item, selectedFilter, fiscalYearRange) => {
  if (selectedFilter === 'all' || !fiscalYearRange?.start || !fiscalYearRange?.end) return true;

  const dateValue = item?.eventDate || item?.occurredAt || item?.createdAt || item?.dueDate;
  if (!dateValue) return selectedFilter !== 'current';

  const itemDate = new Date(dateValue);
  const fiscalStart = new Date(fiscalYearRange.start);
  const fiscalEnd = new Date(fiscalYearRange.end);

  if (Number.isNaN(itemDate.getTime()) || Number.isNaN(fiscalStart.getTime()) || Number.isNaN(fiscalEnd.getTime())) {
    return selectedFilter !== 'current';
  }

  fiscalEnd.setHours(23, 59, 59, 999);
  const inRange = itemDate >= fiscalStart && itemDate <= fiscalEnd;
  return selectedFilter === 'current' ? inRange : !inRange;
};

const ReviewReminders = ({ orgUnitId, performanceData, currentFilter = 'all', selectedFiscalYear = null }) => {
  const engine = useDataEngine();
  const [companyGoalsData, setCompanyGoalsData] = useState({
    totalGoals: 0,
    goalsWithoutObjectives: 0,
    activeGoalsEndDate: null,
    daysUntilNewGoalsCreation: null,
    canCreateNewGoals: false
  });

  const [objectivesData, setObjectivesData] = useState({
    pendingApproval: 0,
    returnedForReview: 0,
    objectivesWithoutTasks: 0,
    objectivesWithoutRecentTasks: 0,
    totalObjectives: 0
  });

  const [kpiSummaryData, setKpiSummaryData] = useState({
    totalKPIs: 0,
    avgProgress: 0,
    onTrackKPIs: 0,
    completedKPIs: 0,
    behindKPIs: 0,
    completionRate: 0
  });

  const [leadershipReminders, setLeadershipReminders] = useState({
    weightsDeadline: null,
    daysUntilWeights: null,
    midYearDeadline: null,
    daysUntilMidYear: null,
    yearEndDeadline: null,
    daysUntilYearEnd: null,
    currentPhase: 'weights',
    fiscalYearStart: null,
    fiscalYearEnd: null,
    isLoading: true
  });

  // Leadership metrics state - dynamically populated
  const [leadershipMetrics, setLeadershipMetrics] = useState({
    hasLeadershipData: false,
    totalCompetencies: 0,
    configuredCompetencies: 0,
    weightsComplete: false,
    totalWeight: 0,
    competencies: [],
    selfAssessment: {
      midYearComplete: 0,
      endYearComplete: 0,
      overallScore: 0,
      pendingApprovals: 0,
      returnedItems: 0,
      completedCompetencies: 0,
      topCompetencies: [],
      needsAttention: []
    },
    teamAssessments: {
      totalStaff: 0,
      pendingApprovals: 0,
      completedAssessments: 0,
      averageTeamScore: 0,
      staffWithData: []
    },
    reviewPhase: 'weights',
    nextDeadline: null,
    daysUntilNextDeadline: null
  });

  // Memoized queries
  const userOrgsQuery = useMemo(() => ({
    me: {
      resource: 'me',
      params: {
        fields: 'organisationUnits[id,name,level]'
      }
    }
  }), []);

  const goalsQuery = useMemo(() => ({
    goals: {
      resource: 'tracker/events',
      params: {
        orgUnit: orgUnitId,
        ouMode: 'ACCESSIBLE',
        program: 'NkReudUAW4F',
        programStage: 'VomTWbJot7U',
        pageSize: 1000,
        fields: 'event,eventDate,dueDate,orgUnitName,orgUnit,dataValues[dataElement,value]',
        order: 'eventDate:desc'
      }
    }
  }), [orgUnitId]);

  const objectivesQuery = useMemo(() => ({
    objectives: {
      resource: 'tracker/events',
      params: {
        orgUnit: orgUnitId,
        ouMode: 'ACCESSIBLE',
        program: 'yiT00XKrrHP',
        programStage: 'VIBtpvkpHFR',
        pageSize: 1000,
        fields: 'event,occurredAt,orgUnit,trackedEntity,dataValues[dataElement,value]',
        order: 'occurredAt:desc'
      }
    }
  }), [orgUnitId]);

  const kpisQuery = useMemo(() => ({
    kpis: {
      resource: 'tracker/events',
      params: {
        orgUnit: orgUnitId,
        ouMode: 'ACCESSIBLE',
        program: 'yiT00XKrrHP',
        programStage: 'ivyCLjNLFrd',
        pageSize: 1000,
        fields: 'event,eventDate,orgUnitName,orgUnit,dataValues[dataElement,value]',
        order: 'eventDate:desc'
      }
    }
  }), [orgUnitId]);

  const tasksQuery = useMemo(() => ({
    tasks: {
      resource: 'tracker/events',
      params: {
        orgUnit: orgUnitId,
        ouMode: 'ACCESSIBLE',
        program: 'yiT00XKrrHP',
        programStage: 'Nv9nYNcBTdM',
        pageSize: 1000,
        fields: 'event,occurredAt,orgUnit,trackedEntity,dataValues[dataElement,value]',
        order: 'createdAt:desc'
      }
    }
  }), [orgUnitId]);

  const leadershipQuery = useMemo(() => ({
    leadership: {
      resource: 'tracker/events',
      params: {
        orgUnit: orgUnitId,
        ouMode: 'ACCESSIBLE',
        program: LEADERSHIP_CONFIG.program,
        pageSize: 2000,
        fields: 'event,programStage,eventDate,trackedEntityInstance,orgUnit,dataValues[dataElement,value]',
        order: 'eventDate:desc'
      }
    }
  }), [orgUnitId]);

  const competencyConfigQuery = useMemo(() => ({
    config: {
      resource: 'tracker/events',
      params: {
        orgUnit: orgUnitId,
        ouMode: 'ACCESSIBLE',
        program: LEADERSHIP_CONFIG.program,
        programStage: LEADERSHIP_CONFIG.stages.config,
        pageSize: 500,
        fields: 'event,dataValues[dataElement,value]',
        order: 'created:desc'
      }
    }
  }), [orgUnitId]);

  const staffQuery = useMemo(() => ({
    staff: {
      resource: 'trackedEntityInstances',
      params: {
        trackedEntityType: 'OaAbpIs1cV7',
        ou: orgUnitId,
        ouMode: 'DESCENDANTS',
        fields: 'trackedEntityInstance,attributes[attribute,value]',
        pageSize: 500
      }
    }
  }), [orgUnitId]);

  // ===== FISCAL YEAR FETCHING =====
  useEffect(() => {
    const loadFiscalYear = async () => {
      if (!orgUnitId || !engine) {
        setLeadershipReminders(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        console.log('🔍 [ReviewReminders] Loading fiscal year for orgUnit:', orgUnitId);

        // Get Staff TEI from enrollments
        const enrollmentsResponse = await engine.query({
          enrollmentsQuery: {
            resource: 'enrollments',
            params: {
              ouMode: 'ACCESSIBLE',
              program: FISCAL_YEAR_CONFIG.program,
              fields: 'trackedEntityInstance',
              pageSize: 1,
              order: 'lastUpdated:desc'
            }
          }
        });

        const teiId = enrollmentsResponse.enrollmentsQuery?.enrollments?.[0]?.trackedEntityInstance;
        if (!teiId) {
          console.warn('⚠️ [ReviewReminders] No Staff TEI found for fiscal year load');
          setLeadershipReminders(prev => ({ ...prev, isLoading: false }));
          return;
        }

        console.log('📋 [ReviewReminders] Found TEI for fiscal year:', teiId);

        // Query metadata event
        const eventsResponse = await engine.query({
          eventsQuery: {
            resource: 'events',
            params: {
              program: FISCAL_YEAR_CONFIG.program,
              programStage: FISCAL_YEAR_CONFIG.stage,
              ouMode: 'ACCESSIBLE',
              trackedEntityInstance: teiId,
              pageSize: 1,
              order: 'eventDate:desc',
              fields: 'dataValues[dataElement,value]'
            }
          }
        });

        const event = eventsResponse.eventsQuery?.events?.[0];
        let start = null, end = null;

        if (event) {
          event.dataValues?.forEach(dv => {
            if (dv.dataElement === FISCAL_YEAR_CONFIG.dataElements.start) start = dv.value;
            if (dv.dataElement === FISCAL_YEAR_CONFIG.dataElements.end) end = dv.value;
          });
        }

        // Resolve active fiscal year from the stored pattern
        let resolvedFy;
        if (start && end) {
          resolvedFy = resolveActiveFiscalYear({ start, end });
        } else {
          resolvedFy = getFallbackFiscalYear();
        }

        const now = new Date();
        const startDate = new Date(resolvedFy.start);
        const yearEndDeadline = new Date(resolvedFy.end);

        // Calculate deadlines based on fiscal year start
        const weightsDeadline = new Date(startDate);
        weightsDeadline.setMonth(weightsDeadline.getMonth() + 1); // End of first month

        const midYearDeadline = new Date(startDate);
        midYearDeadline.setMonth(midYearDeadline.getMonth() + 6); // 6 months in

        // Determine current phase
        let currentPhase = 'weights';
        if (now > weightsDeadline && now <= midYearDeadline) {
          currentPhase = 'midyear';
        } else if (now > midYearDeadline) {
          currentPhase = 'yearend';
        }

        // Calculate days until deadlines
        const daysUntilWeights = calculateDaysUntil(weightsDeadline);
        const daysUntilMidYear = calculateDaysUntil(midYearDeadline);
        const daysUntilYearEnd = calculateDaysUntil(yearEndDeadline);

        const fiscalYearData = {
          weightsDeadline,
          daysUntilWeights,
          midYearDeadline,
          daysUntilMidYear,
          yearEndDeadline,
          daysUntilYearEnd,
          currentPhase,
          fiscalYearStart: startDate,
          fiscalYearEnd: yearEndDeadline,
          isLoading: false
        };

        setLeadershipReminders(fiscalYearData);

        console.log('📅 [ReviewReminders] Fiscal year loaded:', {
          start: startDate.toLocaleDateString(),
          end: yearEndDeadline.toLocaleDateString(),
          weightsDeadline: weightsDeadline.toLocaleDateString(),
          midYearDeadline: midYearDeadline.toLocaleDateString(),
          yearEndDeadline: yearEndDeadline.toLocaleDateString(),
          currentPhase,
          daysUntilWeights,
          daysUntilMidYear,
          daysUntilYearEnd
        });

      } catch (error) {
        console.error('❌ [ReviewReminders] Failed to load fiscal year:', error);

        // Set fallback dates
        const now = new Date();
        const startDate = new Date();
        startDate.setMonth(6);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);

        const weightsDeadline = new Date(startDate);
        weightsDeadline.setMonth(weightsDeadline.getMonth() + 1);

        const midYearDeadline = new Date(startDate);
        midYearDeadline.setMonth(midYearDeadline.getMonth() + 6);

        const yearEndDeadline = new Date(startDate);
        yearEndDeadline.setMonth(yearEndDeadline.getMonth() + 12);

        setLeadershipReminders({
          weightsDeadline,
          daysUntilWeights: calculateDaysUntil(weightsDeadline),
          midYearDeadline,
          daysUntilMidYear: calculateDaysUntil(midYearDeadline),
          yearEndDeadline,
          daysUntilYearEnd: calculateDaysUntil(yearEndDeadline),
          currentPhase: now > midYearDeadline ? 'yearend' : (now > weightsDeadline ? 'midyear' : 'weights'),
          fiscalYearStart: startDate,
          fiscalYearEnd: yearEndDeadline,
          isLoading: false
        });
      }
    };

    loadFiscalYear();
  }, [orgUnitId, engine]);

    
  // Execute queries
  const { loading: userOrgsLoading, error: userOrgsError, data: userOrgsData } = useDataQuery(userOrgsQuery);
  const { loading: goalsLoading, error: goalsError, data: goalsData } = useDataQuery(goalsQuery);
  const { loading: objectivesLoading, error: objectivesError, data: objectivesRawData } = useDataQuery(objectivesQuery);
  const { loading: kpisLoading, error: kpisError, data: kpisRawData } = useDataQuery(kpisQuery);
  const { loading: tasksLoading, error: tasksError, data: tasksRawData } = useDataQuery(tasksQuery);
  const { loading: leadershipLoading, error: leadershipError, data: leadershipRawData } = useDataQuery(leadershipQuery);
  const { loading: configLoading, error: configError, data: configData } = useDataQuery(competencyConfigQuery);
  const { loading: staffLoading, error: staffError, data: staffData } = useDataQuery(staffQuery);
 


  const hasExternalPerformanceData = Boolean(performanceData);

  const allGoals = useMemo(
    () => performanceData?.goals?.events || goalsData?.goals?.instances || [],
    [performanceData, goalsData]
  );

  const allObjectives = useMemo(
    () => performanceData?.objectives?.events || objectivesRawData?.objectives?.instances || [],
    [performanceData, objectivesRawData]
  );

  const allTasks = useMemo(
    () => performanceData?.tasks?.events || tasksRawData?.tasks?.instances || [],
    [performanceData, tasksRawData]
  );

  const allKpis = useMemo(
    () => performanceData?.kpis?.events || kpisRawData?.kpis?.instances || [],
    [performanceData, kpisRawData]
  );

  const allLeadershipEvents = useMemo(
    () => getCollectionItems(leadershipRawData?.leadership).filter(event =>
      isItemWithinSelectedFiscalYear(event, currentFilter, selectedFiscalYear)
    ),
    [leadershipRawData, currentFilter, selectedFiscalYear]
  );

  // Position mapping for objectives
  const positionTEIs = useMemo(() => Array.from(
    new Set(
      allObjectives
        .map(obj =>
          obj.dataValues?.find(dv => dv.dataElement === POSITION_TEI_DE)?.value
        )
        .filter(Boolean)
    )
  ).join(';'), [allObjectives]);


  const positionsQuery = useMemo(() => (
    positionTEIs
      ? {
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: POSITION_TE_TYPE,
            filter: `trackedentityinstance:in:[${positionTEIs}]`,
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            pageSize: positionTEIs.split(';').length,
          },
        },
      }
      : {}
  ), [positionTEIs]);


   const { loading: positionsLoading, data: positionsData } = useDataQuery(positionsQuery);

  // ===== HELPER FUNCTIONS =====

  // Helper: Calculate days between dates
  const calculateDaysUntil = (targetDate) => {
    try {
      const now = new Date();
      const target = new Date(targetDate);
      if (isNaN(now.getTime()) || isNaN(target.getTime())) return 0;
      const diffTime = target - now;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return 0;
    }
  };

  // Helper: Extract task progress from subtasks
  const getTaskProgressFromSubtasks = (taskDataValues) => {
    if (!taskDataValues) return 0;

    const subTasksJson = taskDataValues.find(dv => dv.dataElement === 'YBXgnhyue59')?.value;
    let subTasks = [];

    try {
      subTasks = subTasksJson ? JSON.parse(subTasksJson) : [];
    } catch (e) {
      console.warn('Invalid subtasks JSON in task:', subTasksJson);
      subTasks = [];
    }

    if (subTasks.length > 0) {
      const totalProgress = subTasks.reduce((sum, st) => {
        const progress = typeof st.progressPercent === 'string'
          ? parseFloat(st.progressPercent)
          : st.progressPercent;
        return sum + (Number.isFinite(progress) ? progress : 0);
      }, 0);
      return Math.round(totalProgress / subTasks.length);
    }

    const topLevelProgress = taskDataValues.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value;
    if (topLevelProgress !== undefined && topLevelProgress !== null) {
      return parseInt(topLevelProgress, 10) || 0;
    }

    return 0;
  };

  // Helper: Calculate KPI progress
  const calculateKPIProgress = (kpi, allTasks) => {
    if (!allTasks || allTasks.length === 0) return 0;

    const kpiDataValues = kpi.dataValues || [];
    const objectiveId = kpiDataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;

    if (!objectiveId) return 0;

    const objectiveTasks = allTasks.filter(task => {
      const taskDataValues = task.dataValues || [];
      const taskObjectiveId = taskDataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
      return taskObjectiveId === objectiveId;
    });

    if (objectiveTasks.length === 0) return 0;

    const taskProgresses = objectiveTasks.map(task => {
      const taskProgress = getTaskProgressFromSubtasks(task.dataValues);
      const taskDataValues = task.dataValues || [];
      const status = taskDataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      const isCompleted = status === 'completed' || status === 'Completed';
      return isCompleted ? 100 : taskProgress;
    });

    const avgProgress = taskProgresses.reduce((sum, p) => sum + p, 0) / taskProgresses.length;
    const completedTasks = taskProgresses.filter(p => p >= 95).length;
    const completionRate = (completedTasks / taskProgresses.length) * 100;

    const weightedProgress = (completionRate * 0.7) + (avgProgress * 0.3);
    return Math.round(Math.min(weightedProgress, 100));
  };

  // Helper: Get staff name from TEI
  const getStaffName = (teiId) => {
    if (!teiId || !staffData?.staff?.trackedEntityInstances) return 'Unknown';
    const staff = staffData.staff.trackedEntityInstances.find(s => s.trackedEntityInstance === teiId);
    if (!staff) return 'Unknown';

    const firstName = staff.attributes?.find(a => a.attribute === 'sB1IHYu2xQT')?.value;
    const surname = staff.attributes?.find(a => a.attribute === 'GQ2R64JdMbK')?.value;

    if (firstName && surname) return `${firstName} ${surname}`;
    if (firstName) return firstName;
    return teiId.substring(0, 8);
  };

  // ===== PROCESS COMPANY GOALS DATA =====
  useEffect(() => {
      const goals = allGoals;
      const objectives = allObjectives;
      const totalGoals = goals.length;

      const orgUnitObjectives = new Map();
      objectives.forEach(obj => {
        const orgUnitId = obj.orgUnit;
        if (!orgUnitObjectives.has(orgUnitId)) {
          orgUnitObjectives.set(orgUnitId, []);
        }
        orgUnitObjectives.get(orgUnitId).push(obj);
      });

      let goalsWithoutObjectives = 0;
      goals.forEach(goal => {
        const goalOrgUnit = goal.orgUnit;
        const objectivesInOrgUnit = orgUnitObjectives.get(goalOrgUnit) || [];
        const hasObjectives = objectivesInOrgUnit.length > 0;
        if (!hasObjectives) {
          goalsWithoutObjectives++;
        }
      });

      const goalsWithEndDates = goals.filter(goal => {
        const dataValues = goal.dataValues || [];
        const endDate = dataValues.find(dv =>
          dv.dataElement === 'J0rjxrDKn1l' ||
          dv.dataElement === 'pUvJQ9vy3A3' ||
          dv.dataElement === 'dueDate'
        )?.value;
        return endDate;
      });

      const sortedGoals = [...goalsWithEndDates].sort((a, b) => {
        const getGoalDate = (goal) => {
          const dataValues = goal.dataValues || [];
          return dataValues.find(dv =>
            dv.dataElement === 'J0rjxrDKn1l' ||
            dv.dataElement === 'pUvJQ9vy3A3' ||
            dv.dataElement === 'dueDate'
          )?.value;
        };
        const aDate = getGoalDate(a);
        const bDate = getGoalDate(b);
        return new Date(bDate) - new Date(aDate);
      });

      const activeGoalsEndDate = sortedGoals[0] ?
        sortedGoals[0].dataValues.find(dv =>
          dv.dataElement === 'J0rjxrDKn1l' ||
          dv.dataElement === 'pUvJQ9vy3A3' ||
          dv.dataElement === 'dueDate'
        )?.value : null;

      let daysUntilNewGoalsCreation = null;
      let canCreateNewGoals = false;

      if (activeGoalsEndDate) {
        const endDate = new Date(activeGoalsEndDate);
        const newGoalsDate = new Date(endDate);
        newGoalsDate.setDate(newGoalsDate.getDate() + 30);
        daysUntilNewGoalsCreation = calculateDaysUntil(newGoalsDate);
        canCreateNewGoals = daysUntilNewGoalsCreation <= 0;
      } else if (totalGoals === 0) {
        canCreateNewGoals = true;
        daysUntilNewGoalsCreation = 0;
      }

      setCompanyGoalsData({
        totalGoals,
        goalsWithoutObjectives,
        activeGoalsEndDate,
        daysUntilNewGoalsCreation,
        canCreateNewGoals
      });
  }, [allGoals, allObjectives]);

  // ===== PROCESS OBJECTIVES DATA =====
  useEffect(() => {
    if (!allObjectives.length) {
      setObjectivesData({
        pendingApproval: 0,
        returnedForReview: 0,
        objectivesWithoutTasks: 0,
        objectivesWithoutRecentTasks: 0,
        totalObjectives: 0
      });
      return;
    }

    const positionMap = new Map();
    if (positionsData?.positions?.trackedEntityInstances) {
      positionsData.positions.trackedEntityInstances.forEach(pos => {
        const attrs = new Map(
          (pos.attributes || []).map(attr => [attr.attribute, attr.value])
        );
        const hasReportingTo = attrs.has(REPORTING_TO_ATTR) && attrs.get(REPORTING_TO_ATTR);
        positionMap.set(pos.trackedEntityInstance, !!hasReportingTo);
      });
    }

    const objectives = allObjectives;
    const totalObjectives = objectives.length;

    let pendingApproval = 0;
    let returnedForReview = 0;

    objectives.forEach(obj => {
      const dataValues = obj.dataValues || [];
      const approvalStatus = dataValues.find(dv => dv.dataElement === 'DzQorireRBf')?.value;
      const positionTEI = dataValues.find(dv => dv.dataElement === POSITION_TEI_DE)?.value;
      const isTopLevel = positionTEI ? !positionMap.get(positionTEI) : false;

      if (isTopLevel) return;

      if (
        approvalStatus === undefined ||
        ['pending', 'submitted', 'Pending', 'Submitted'].includes(approvalStatus)
      ) {
        pendingApproval++;
      } else if (['returned', 'rejected', 'Returned', 'Rejected'].includes(approvalStatus)) {
        returnedForReview++;
      }
    });

    const objectivesWithTasks = new Set();
    const objectivesWithRecentTasks = new Set();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    if (allTasks.length) {
      allTasks.forEach(task => {
        const dataValues = task.dataValues || [];
        const objectiveId = dataValues.find(dv => dv.dataElement === TASK_TO_OBJECTIVE_DE)?.value;
        if (objectiveId) {
          objectivesWithTasks.add(objectiveId);

          const selectedWeekValue = dataValues.find(dv => dv.dataElement === 'EiL3lPaL908')?.value;
          if (selectedWeekValue) {
            const taskWeekDate = new Date(selectedWeekValue);
            taskWeekDate.setHours(0, 0, 0, 0);
            if (taskWeekDate >= sevenDaysAgo) {
              objectivesWithRecentTasks.add(objectiveId);
            }
          }
        }
      });
    }

    const objectivesWithoutTasks = objectives.filter(obj =>
      !objectivesWithTasks.has(obj.event)
    ).length;

    const objectivesWithoutRecentTasks = objectives.filter(obj =>
      !objectivesWithRecentTasks.has(obj.event)
    ).length;

    setObjectivesData({
      pendingApproval,
      returnedForReview,
      objectivesWithoutTasks,
      objectivesWithoutRecentTasks,
      totalObjectives
    });
  }, [allObjectives, allTasks, positionsData]);

  // ===== PROCESS KPI DATA =====
  useEffect(() => {
    if (!allKpis.length) {
      setKpiSummaryData({
        totalKPIs: 0,
        avgProgress: 0,
        onTrackKPIs: 0,
        completedKPIs: 0,
        behindKPIs: 0,
        completionRate: 0
      });
      return;
    }

      const kpis = allKpis;
      const tasks = allTasks;

      const totalKPIs = kpis.length;

      const kpisWithProgress = kpis.map(kpi => {
        const progress = calculateKPIProgress(kpi, tasks);
        return {
          ...kpi,
          progress
        };
      });

      const totalProgress = kpisWithProgress.reduce((sum, kpi) => sum + kpi.progress, 0);
      const avgProgress = totalKPIs > 0 ? Math.round(totalProgress / totalKPIs) : 0;

      const onTrackKPIs = kpisWithProgress.filter(kpi => kpi.progress >= 70).length;
      const completedKPIs = kpisWithProgress.filter(kpi => kpi.progress >= 95).length;
      const behindKPIs = kpisWithProgress.filter(kpi => kpi.progress < 50).length;

      const completionRate = totalKPIs > 0 ? Math.round((completedKPIs / totalKPIs) * 100) : 0;

      setKpiSummaryData({
        totalKPIs,
        avgProgress,
        onTrackKPIs,
        completedKPIs,
        behindKPIs,
        completionRate
      });
  }, [allKpis, allTasks]);

  // ===== PROCESS LEADERSHIP DATA =====
  useEffect(() => {
    console.log('🔍 Processing leadership data...');

    const leadershipEvents = allLeadershipEvents;

    if (leadershipEvents.length === 0) {
      console.log('❌ No leadership events found');
      setLeadershipMetrics({
        hasLeadershipData: false,
        totalCompetencies: 0,
        configuredCompetencies: 0,
        weightsComplete: false,
        totalWeight: 0,
        competencies: [],
        selfAssessment: {
          midYearComplete: 0,
          endYearComplete: 0,
          overallScore: 0,
          pendingApprovals: 0,
          returnedItems: 0,
          completedCompetencies: 0,
          topCompetencies: [],
          needsAttention: []
        },
        teamAssessments: {
          totalStaff: 0,
          pendingApprovals: 0,
          completedAssessments: 0,
          averageTeamScore: 0,
          staffWithData: []
        },
        reviewPhase: leadershipReminders.currentPhase,
        nextDeadline: leadershipReminders.weightsDeadline,
        daysUntilNextDeadline: leadershipReminders.daysUntilWeights
      });
      return;
    }

    console.log(`📊 Found ${leadershipEvents.length} leadership events`);

    // Get competencies from config or use defaults
    const configEvents = configData?.config?.instances || [];
    let competencyNames = [];

    if (configEvents.length > 0) {
      configEvents.forEach(event => {
        const dataValues = event.dataValues || [];
        const codeDV = dataValues.find(dv => dv.dataElement === LEADERSHIP_CONFIG.de.competencyCode);
        if (codeDV?.value && !competencyNames.includes(codeDV.value)) {
          competencyNames.push(codeDV.value);
        }
      });
    }

    if (competencyNames.length === 0) {
      competencyNames = ['RESPECT', 'EXCELLENCE', 'LEARNING', 'INTEGRITY'];
      console.log('Using default competencies:', competencyNames);
    }

    console.log(`📊 Competencies:`, competencyNames);

    // Group events by TEI
    const staffCompetencies = {};
    let untrackedEvents = 0;

    leadershipEvents.forEach(event => {
      let teiId = event.trackedEntityInstance;

      if (!teiId) {
        teiId = `untracked-${event.event}`;
        untrackedEvents++;
      }

      const dataValues = event.dataValues || [];
      const programStage = event.programStage;

      const ratings = [];
      dataValues.forEach(dv => {
        const num = parseInt(dv.value);
        if (!isNaN(num) && num >= 1 && num <= 5) {
          ratings.push({
            dataElement: dv.dataElement,
            value: num
          });
        }
      });

      if (ratings.length === 0) return;

      if (!staffCompetencies[teiId]) {
        staffCompetencies[teiId] = {
          teiId,
          competencies: {},
          midYearRatings: [],
          endYearRatings: [],
          lastReview: event.eventDate
        };

        competencyNames.forEach(name => {
          staffCompetencies[teiId].competencies[name] = {
            name,
            weight: 0.25,
            midYear: null,
            endYear: null,
            raw: 0
          };
        });
      }

      if (programStage === LEADERSHIP_CONFIG.stages.midYear) {
        staffCompetencies[teiId].midYearRatings = ratings;
      } else if (programStage === LEADERSHIP_CONFIG.stages.endYear) {
        staffCompetencies[teiId].endYearRatings = ratings;
      }
    });

    console.log(`📊 Events without TEI: ${untrackedEvents}`);

    Object.keys(staffCompetencies).forEach(teiId => {
      const staff = staffCompetencies[teiId];

      if (staff.midYearRatings.length > 0) {
        staff.midYearRatings.forEach((rating, index) => {
          if (index < competencyNames.length) {
            const compName = competencyNames[index];
            staff.competencies[compName].midYear = rating.value;
            staff.competencies[compName].raw = rating.value;
          }
        });
      }

      if (staff.endYearRatings.length > 0) {
        staff.endYearRatings.forEach((rating, index) => {
          if (index < competencyNames.length) {
            const compName = competencyNames[index];
            staff.competencies[compName].endYear = rating.value;
            staff.competencies[compName].raw = rating.value;
          }
        });
      }
    });

    const staffList = Object.values(staffCompetencies);

    staffList.forEach(staff => {
      let totalWeightedScore = 0;
      let competencyCount = 0;

      Object.values(staff.competencies).forEach(comp => {
        const score = comp.midYear || comp.endYear || 0;
        if (score > 0) {
          totalWeightedScore += score * (comp.weight || 0.25);
          competencyCount++;
        }
      });

      staff.totalScore = competencyCount > 0
        ? Math.round((totalWeightedScore / competencyCount) * 100) / 100
        : 0;
    });

    console.log('👥 Staff processed:', staffList.length);

    if (staffList.length === 0) {
      console.log('❌ No staff with ratings found');
      return;
    }

    const avgScores = {};
    const avgRawScores = {};

    competencyNames.forEach(name => {
      let totalWeighted = 0;
      let totalRaw = 0;
      let count = 0;

      staffList.forEach(staff => {
        const comp = staff.competencies[name];
        const score = comp.midYear || comp.endYear || 0;
        if (score > 0) {
          totalWeighted += score * (comp.weight || 0.25);
          totalRaw += score;
          count++;
        }
      });

      avgScores[name] = count > 0 ? totalWeighted / count : 0;
      avgRawScores[name] = count > 0 ? totalRaw / count : 0;
    });

    const overallScore = Object.values(avgScores).reduce((sum, s) => sum + s, 0);

    const competenciesList = competencyNames.map(name => ({
      name,
      weight: 0.25,
      score: avgScores[name] || 0,
      rawScore: avgRawScores[name] || 0
    }));

    const sortedByRaw = Object.entries(avgRawScores)
      .map(([name, score]) => ({ name, score: Math.round(score * 10) / 10 }))
      .sort((a, b) => b.score - a.score);

    const topCompetencies = sortedByRaw.slice(0, 2).filter(c => c.score > 0);
    const needsAttention = sortedByRaw
      .filter(c => c.score < 3 && c.score > 0)
      .slice(0, 2);

    const staffWithData = staffList.map(staff => ({
      name: staff.teiId.startsWith('untracked-') ? 'Unassigned Staff' : getStaffName(staff.teiId),
      score: staff.totalScore,
      midYearCount: staff.midYearRatings.length,
      endYearCount: staff.endYearRatings.length,
      lastReview: staff.lastReview
    }));

    console.log('📊 Final averages:', {
      competencies: competenciesList,
      overallScore,
      staffCount: staffList.length
    });

    setLeadershipMetrics({
      hasLeadershipData: true,
      totalCompetencies: competencyNames.length,
      configuredCompetencies: competencyNames.length,
      weightsComplete: true,
      totalWeight: 100,
      competencies: competenciesList,
      selfAssessment: {
        midYearComplete: staffList.filter(s => s.midYearRatings.length > 0).length,
        endYearComplete: staffList.filter(s => s.endYearRatings.length > 0).length,
        overallScore: Math.round(overallScore * 100) / 100,
        pendingApprovals: 0,
        returnedItems: 0,
        completedCompetencies: competencyNames.length,
        topCompetencies,
        needsAttention
      },
      teamAssessments: {
        totalStaff: staffList.length,
        pendingApprovals: 0,
        completedAssessments: staffList.length,
        averageTeamScore: Math.round(overallScore * 100) / 100,
        staffWithData
      },
      reviewPhase: leadershipReminders.currentPhase,
      nextDeadline: leadershipReminders.weightsDeadline,
      daysUntilNextDeadline: leadershipReminders.daysUntilWeights
    });

  }, [allLeadershipEvents, configData, staffData, leadershipReminders.currentPhase]);

  // ===== LOADING STATES =====
  const isLoading = leadershipReminders.isLoading || (!hasExternalPerformanceData && (goalsLoading || objectivesLoading || kpisLoading || tasksLoading)) || leadershipLoading || configLoading || userOrgsLoading || positionsLoading || staffLoading;
  const error = (!hasExternalPerformanceData && (goalsError || objectivesError || kpisError || tasksError)) || leadershipError || configError || userOrgsError || staffError;

  // ===== FORMATTING FUNCTIONS =====
  const formatDaysDisplay = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days < 0) return `${Math.abs(days)} days ago`;
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.ceil(days / 7)} weeks`;
    return `${Math.ceil(days / 30)} months`;
  };

  const formatDateDisplay = (date) => {
    if (!date) return 'Not set';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return 'Invalid date';
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getProgressColor = (value) => {
    if (value >= 80) return '#28a745';
    if (value >= 50) return '#ffc107';
    return '#dc3545';
  };

  const getScoreColor = (score) => {
    if (score >= 4) return '#28a745';
    if (score >= 3) return '#ffc107';
    return '#dc3545';
  };

  // ===== RENDER =====
  if (isLoading) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 20,
        marginBottom: 20,
        width: '100%'
      }}>
        {[1, 2, 3, 4, 5].map(i => (
          <Card key={i} style={{ padding: 16 }}>
            <h3>Loading...</h3>
            <CircularLoader small />
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 20,
        marginBottom: 20,
        width: '100%'
      }}>
        <Card style={{ padding: 16, backgroundColor: '#f8d7da' }}>
          <h3>Review Reminders</h3>
          <div style={{ color: '#721c24' }}>
            Error loading data: {error.message}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 20,
      marginBottom: 20,
      width: '100%'
    }}>
      {/* Card 1: Company Goals Overview */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgba(240, 229, 203, 1)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#c18c06ff',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          Company Goals Overview
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#fff3cd',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>
                  {companyGoalsData.totalGoals}
                </div>
                <div style={{ fontSize: '10px', color: '#856404' }}>Total Goals</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: companyGoalsData.goalsWithoutObjectives > 0 ? '#dc3545' : '#28a745'
                }}>
                  {companyGoalsData.goalsWithoutObjectives}
                </div>
                <div style={{ fontSize: '10px', color: '#856404' }}>Without Objectives</div>
              </div>
            </div>

            {companyGoalsData.activeGoalsEndDate && (
              <div style={{
                fontSize: '12px',
                color: '#856404',
                marginBottom: '8px',
                padding: '8px',
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <strong>Current Goals End:</strong><br />
                {formatDateDisplay(companyGoalsData.activeGoalsEndDate)}
              </div>
            )}

            {companyGoalsData.daysUntilNewGoalsCreation !== null && (
              <div style={{
                fontSize: '12px',
                color: companyGoalsData.canCreateNewGoals ? '#28a745' : '#856404',
                marginBottom: '8px',
                padding: '8px',
                backgroundColor: companyGoalsData.canCreateNewGoals ? '#d4edda' : 'rgba(255,255,255,0.7)',
                borderRadius: '6px',
                fontWeight: companyGoalsData.canCreateNewGoals ? '600' : 'normal',
                textAlign: 'center'
              }}>
                {companyGoalsData.canCreateNewGoals ? (
                  '✅ Ready to create new goals!'
                ) : (
                  `⏰ New goals in: ${formatDaysDisplay(companyGoalsData.daysUntilNewGoalsCreation)}`
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card 2: Objectives Management */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgba(190, 248, 229, 1)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#069362ff',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          Objectives Overview
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#d4f7e6ff',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: objectivesData.pendingApproval > 0 ? '#dc3545' : '#28a745' }}>
                  {objectivesData.pendingApproval}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Pending Approval</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: objectivesData.returnedForReview > 0 ? '#ffc107' : '#28a745' }}>
                  {objectivesData.returnedForReview}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Returned for Review</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: objectivesData.objectivesWithoutTasks > 0 ? '#dc3545' : '#28a745' }}>
                  {objectivesData.objectivesWithoutTasks}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Without Tasks</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: objectivesData.objectivesWithoutRecentTasks > 0 ? '#ffc107' : '#28a745' }}>
                  {objectivesData.objectivesWithoutRecentTasks}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Without Recent Tasks</div>
              </div>
            </div>

            <div style={{
              fontSize: '11px',
              color: '#6c757d',
              padding: '6px',
              backgroundColor: 'rgba(255,255,255,0.7)',
              borderRadius: '4px',
              textAlign: 'center'
            }}>
              Total: {objectivesData.totalObjectives} Objectives
            </div>
          </div>
        </div>
      </div>

      {/* Card 3: KPI Performance Summary */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgba(173, 216, 230, 1)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#1e6ea7',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          KPIs Summary
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#e3f2fd',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#1e6ea7'
                }}>
                  {kpiSummaryData.totalKPIs}
                </div>
                <div style={{ fontSize: '9px', color: '#1e6ea7' }}>Total KPIs</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: getProgressColor(kpiSummaryData.avgProgress)
                }}>
                  {kpiSummaryData.avgProgress}%
                </div>
                <div style={{ fontSize: '9px', color: '#1e6ea7' }}>Avg Progress</div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: kpiSummaryData.onTrackKPIs > 0 ? '#28a745' : '#6c757d'
              }}>
                {kpiSummaryData.onTrackKPIs}
              </div>
              <div style={{ fontSize: '9px', color: '#1e6ea7' }}>On Track KPIs</div>
            </div>

            <div style={{
              marginTop: '8px',
              padding: '6px',
              backgroundColor: 'rgba(255,255,255,0.7)',
              borderRadius: '4px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '10px',
                color: '#1e6ea7',
                marginBottom: '2px'
              }}>
                <span>Overall Progress</span>
                <span>{kpiSummaryData.avgProgress}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: '#e9ecef',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${kpiSummaryData.avgProgress}%`,
                  height: '100%',
                  backgroundColor: getProgressColor(kpiSummaryData.avgProgress),
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            <div style={{
              marginTop: '8px',
              fontSize: '10px',
              color: '#1e6ea7',
              textAlign: 'center',
              padding: '4px',
              backgroundColor: 'rgba(255,255,255,0.5)',
              borderRadius: '4px'
            }}>
              {kpiSummaryData.completedKPIs} completed • {kpiSummaryData.behindKPIs} behind
            </div>
          </div>
        </div>
      </div>

      {/* Card 4: Leadership Assessment Status 
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgb(240, 221, 203)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#c17b42',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          Leadership Assessment
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#fde2d8',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            {leadershipMetrics.hasLeadershipData ? (
              <>
                
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>Overall Company Average</div>
                  <div style={{
                    fontSize: '22px',
                    fontWeight: 'bold',
                    color: getScoreColor(leadershipMetrics.selfAssessment.overallScore)
                  }}>
                    {leadershipMetrics.selfAssessment.overallScore.toFixed(2)}
                  </div>
                </div>

               
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(leadershipMetrics.competencies.length, 2)}, 1fr)`, gap: '8px', marginBottom: '12px' }}>
                  {leadershipMetrics.competencies.slice(0, 4).map((comp, idx) => (
                    <Tooltip key={idx} content={`Weight: ${Math.round(comp.weight * 100)}% | Raw: ${comp.rawScore.toFixed(1)}/5`}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: '#6c757d' }}>{comp.name}</div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: getScoreColor(comp.rawScore)
                        }}>
                          {comp.score.toFixed(2)}
                        </div>
                      </div>
                    </Tooltip>
                  ))}
                </div>

              
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Tooltip content="Staff with completed assessments">
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#6f42c1' }}>
                        {leadershipMetrics.teamAssessments.totalStaff}
                      </div>
                      <div style={{ fontSize: '9px', color: '#6c757d' }}>Staff Assessed</div>
                    </div>
                  </Tooltip>
                  <Tooltip content="Average team score">
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: getScoreColor(leadershipMetrics.teamAssessments.averageTeamScore)
                      }}>
                        {leadershipMetrics.teamAssessments.averageTeamScore.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '9px', color: '#6c757d' }}>Team Avg</div>
                    </div>
                  </Tooltip>
                </div>

             
                {leadershipMetrics.selfAssessment.topCompetencies.length > 0 && (
                  <div style={{ fontSize: '10px', marginBottom: '8px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>🌟 Top Strengths:</div>
                    {leadershipMetrics.selfAssessment.topCompetencies.map((comp, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                        <span style={{ color: '#6c757d' }}>{comp.name}</span>
                        <span style={{ fontWeight: 'bold', color: getScoreColor(comp.score) }}>{comp.score.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                )}

               
                {leadershipMetrics.selfAssessment.needsAttention.length > 0 && (
                  <div style={{ fontSize: '10px', marginBottom: '8px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#dc3545' }}>⚠️ Needs Attention:</div>
                    {leadershipMetrics.selfAssessment.needsAttention.map((comp, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                        <span style={{ color: '#6c757d' }}>{comp.name}</span>
                        <span style={{ fontWeight: 'bold', color: '#dc3545' }}>{comp.score.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                )}

               
                <div style={{
                  fontSize: '11px',
                  color: '#6c757d',
                  padding: '6px',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  borderRadius: '4px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  textAlign: 'center'
                }}>
                  <span>Mid-Year: {leadershipMetrics.selfAssessment.midYearComplete} • End-Year: {leadershipMetrics.selfAssessment.endYearComplete}</span>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#6c757d', fontSize: '12px', padding: '20px 0' }}>
                No leadership data available
              </div>
            )}
          </div>
        </div>
      </div>
      */}

      {/* Card 5: Leadership Review Schedule */}
      {/* 
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgba(203, 195, 227, 1)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#5e35b1',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          Review Schedule
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#ede7f6',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontSize: '12px',
                color: leadershipReminders.currentPhase === 'weights' ? '#5e35b1' : '#6c757d',
                marginBottom: '6px',
                fontWeight: leadershipReminders.currentPhase === 'weights' ? '600' : 'normal',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span> <ChartLine size={16} /> Weight Targets</span>
                <span style={{ fontSize: '11px', fontWeight: 'bold' }}>
                  {formatDaysDisplay(leadershipReminders.daysUntilWeights)}
                </span>
              </div>
              <div style={{
                fontSize: '10px',
                color: '#6c757d',
                marginBottom: '8px',
                textAlign: 'center'
              }}>
                Due: {formatDateDisplay(leadershipReminders.weightsDeadline)}
              </div>

              <div style={{
                fontSize: '12px',
                color: leadershipReminders.currentPhase === 'midyear' ? '#5e35b1' : '#6c757d',
                marginBottom: '6px',
                fontWeight: leadershipReminders.currentPhase === 'midyear' ? '600' : 'normal',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span> <Eye size={16} /> Mid-Year Review</span>
                <span style={{ fontSize: '11px', fontWeight: 'bold' }}>
                  {formatDaysDisplay(leadershipReminders.daysUntilMidYear)}
                </span>
              </div>
              <div style={{
                fontSize: '10px',
                color: '#6c757d',
                marginBottom: '8px',
                textAlign: 'center'
              }}>
                Due: {formatDateDisplay(leadershipReminders.midYearDeadline)}
              </div>

              <div style={{
                fontSize: '12px',
                color: leadershipReminders.currentPhase === 'yearend' ? '#5e35b1' : '#6c757d',
                fontWeight: leadershipReminders.currentPhase === 'yearend' ? '600' : 'normal',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span><Eye size={16} /> Year-End Review</span>
                <span style={{ fontSize: '11px', fontWeight: 'bold' }}>
                  {formatDaysDisplay(leadershipReminders.daysUntilYearEnd)}
                </span>
              </div>
              <div style={{
                fontSize: '10px',
                color: '#6c757d',
                textAlign: 'center'
              }}>
                Due: {formatDateDisplay(leadershipReminders.yearEndDeadline)}
              </div>
            </div>

            <div style={{
              fontSize: '11px',
              color: '#6c757d',
              padding: '8px',
              backgroundColor: 'rgba(255,255,255,0.7)',
              borderRadius: '6px',
              border: '1px solid rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              {leadershipReminders.currentPhase === 'weights' && '⏰ Set weight targets for the year'}
              {leadershipReminders.currentPhase === 'midyear' && '📊 Complete mid-year performance reviews'}
              {leadershipReminders.currentPhase === 'yearend' && '🎯 Conduct year-end performance assessments'}
            </div>
          </div>
        </div>
      </div>
      */}
      
    </div>
  );
};

export default ReviewReminders;
